import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import authService from '../../shared/services/authService';
import ModuleForm from '../../shared/components/ModuleForm';
import GroupForm from '../../components/GroupForm';

/** Admin Modules & Groups management (extracted from the old tabbed dashboard). */
const ModulesPage: React.FC = () => {
  const [modules, setModules] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [moduleFormOpen, setModuleFormOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<any | null>(null);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);

  const [del, setDel] = useState<{ kind: 'module' | 'group'; id: string; name: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const [m, g] = await Promise.all([authService.getAdminModules(), authService.getGroups()]);
      setModules(Array.isArray(m) ? m : []);
      setGroups(Array.isArray(g) ? g : []);
    } catch (e) {
      toast.error('Failed to load modules');
      setModules([]);
      setGroups([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async () => {
    if (!del) return;
    try {
      const res = del.kind === 'module'
        ? await authService.deleteModule(del.id)
        : await authService.deleteGroup(del.id);
      const msg = (res && (res.message || res.action)) ? `${res.message || ''}`.trim() : `${del.kind} removed`;
      toast.success(msg || `${del.kind} removed`);
      load();
    } catch (e: any) {
      toast.error(e.response?.data?.detail || `Failed to delete ${del.kind}`);
    } finally {
      setDel(null);
    }
  };

  return (
    <Box>
      {/* Modules */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600}>Modules</Typography>
        <Box display="flex" gap={1.5}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedModule(null); setModuleFormOpen(true); }}>
            Add Module
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Auth</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {modules.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>No modules.</Typography></TableCell></TableRow>
              ) : modules.map((m) => (
                <TableRow key={m.id}>
                  <TableCell><Typography variant="subtitle2" fontWeight="bold">{m.name}</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{m.api_endpoint || m.endpoint_url || '—'}</Typography></TableCell>
                  <TableCell>{m.auth_type || '—'}</TableCell>
                  <TableCell>
                    <Chip label={m.enabled ? 'Enabled' : 'Disabled'} color={m.enabled ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => { setSelectedModule(m); setModuleFormOpen(true); }}><EditIcon /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDel({ kind: 'module', id: m.id, name: m.name })}><DeleteIcon /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Groups */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={600}>User Groups</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedGroup(null); setGroupFormOpen(true); }}>
          Add Group
        </Button>
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Members</TableCell>
              <TableCell>Modules</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>No groups.</Typography></TableCell></TableRow>
            ) : groups.map((g) => (
              <TableRow key={g.id}>
                <TableCell><Typography variant="subtitle2" fontWeight="bold">{g.name}</Typography></TableCell>
                <TableCell>{g.description || '—'}</TableCell>
                <TableCell>{g.member_count ?? 0}</TableCell>
                <TableCell>{g.module_count ?? 0}</TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => { setSelectedGroup(g); setGroupFormOpen(true); }}><EditIcon /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDel({ kind: 'group', id: g.id, name: g.name })}><DeleteIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ModuleForm open={moduleFormOpen} onClose={(r) => { setModuleFormOpen(false); setSelectedModule(null); if (r) load(); }} module={selectedModule} />
      <GroupForm open={groupFormOpen} onClose={(r) => { setGroupFormOpen(false); setSelectedGroup(null); if (r) load(); }} group={selectedGroup} />

      <Dialog open={!!del} onClose={() => setDel(null)} maxWidth="xs">
        <DialogTitle>Delete {del?.kind}</DialogTitle>
        <DialogContent><Typography>Delete <strong>{del?.name}</strong>? This cannot be undone.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDel(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModulesPage;
